import { Link } from 'react-router-dom';
import { Shield, CreditCard, Trash2 } from 'lucide-react';
import { GithubIcon } from '@/components/icons/BrandIcons';
import { useInView } from '@/hooks/useInView';
import { useGitHubLogin } from '@/hooks/useGitHubLogin';

export default function ProfessionalCTA() {
  const { isLoading, handleGitHubLogin } = useGitHubLogin();
  const [sectionRef, isInView] = useInView<HTMLElement>({ threshold: 0.2 });

  const trustBadges = [
    { icon: Shield, text: 'Read-only access' },
    { icon: CreditCard, text: 'No credit card' },
    { icon: Trash2, text: 'Delete anytime' },
  ];

  return (
    <section
      ref={sectionRef}
      id="final-cta"
      className="py-16 bg-gray-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          {/* Header */}
          <h2
            className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 ${
              isInView ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            Your Board Asks &ldquo;Why Are We Slow?&rdquo; Have the Answer Ready.
          </h2>

          {/* Subheadline */}
          <p
            className={`text-xl text-gray-300 mb-10 ${
              isInView ? 'animate-fade-in-up animation-delay-100' : 'opacity-0'
            }`}
          >
            Connect your GitHub and get your Engineering Health Report in 15 minutes.
          </p>

          {/* CTA Button */}
          <button
            onClick={() => handleGitHubLogin('professional_cta')}
            disabled={isLoading}
            className={`inline-flex items-center gap-3 px-10 py-5 bg-primary-500 hover:bg-primary-600 text-white rounded-xl text-xl lg:text-2xl font-semibold transition-all shadow-[0_0_30px_rgba(14,165,233,0.3)] hover:shadow-[0_0_40px_rgba(14,165,233,0.4)] disabled:opacity-50 disabled:cursor-not-allowed mb-8 ${
              isInView ? 'animate-scale-in animation-delay-200' : 'opacity-0'
            }`}
            style={{ animationFillMode: 'forwards' }}
          >
            <GithubIcon className="w-6 h-6" />
            {isLoading ? 'Connecting...' : 'Find Your #1 Bottleneck'}
          </button>

          {/* Trust Badges */}
          <div
            className={`flex flex-wrap justify-center gap-x-8 gap-y-3 ${
              isInView ? 'animate-fade-in-up animation-delay-300' : 'opacity-0'
            }`}
          >
            {trustBadges.map((badge, index) => (
              <div key={index} className="flex items-center gap-2 text-gray-400">
                <badge.icon className="w-4 h-4" />
                <span className="text-sm">{badge.text}</span>
              </div>
            ))}
          </div>

          {/* Pricing Signal */}
          <p className="text-gray-500 text-sm mt-4">
            Free up to 10 developers · Pro from $149/mo (annual) ·{' '}
            <Link to="/pricing" className="underline text-gray-400 hover:text-white transition-colors">
              View all plans
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
